import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Select } from "antd";
import {
  addCoupons,
  getBuilderList,
  getProjectListByBuilder,
  getCategoryList,
  getUnitList,
} from "../../Redux/Actions/couponsAction";
import { getEnums } from "../../Redux/Actions/enumsActions";
import { AgentList } from "../../Redux/Actions/agentActions";
import { viewCountryListPagination } from "../../Redux/Actions/settingsActions";
import { getUsersList } from "../../Redux/Actions/usersActions";
import { viewPropertyList } from "../../Redux/Actions/rentalPropertiesActions";
import { ToFirstCapital } from "../../Utils/helpers";
import "./addCoupons.css";

const DROPDOWN_PAGE_LIMIT = 20;

const AddCoupons = () => {
  const [couponState, setCouponState] = useState({
    code: Math.random().toString(36).substring(2, 18).toUpperCase(),
    project_id: "",
    project_type: "",
    no_of_usage: "",
    country: "",
    start_date: "",
    end_date: "",
    discount_type: "",
    discount_in: "",
    value: "",
    value_upto_show: "",
    max_value: "",
    description: "",
    use_for: "",
    agent_id: "",
  });

  // which entity this coupon applies to
  const [entityType, setEntityType] = useState("project"); // "project" | "rental_property"

  // rental property flow selections
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRentalPropertyId, setSelectedRentalPropertyId] = useState("");

  // ── User dropdown: search + infinite scroll pagination state ──
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const [userFetching, setUserFetching] = useState(false);
  const userSearchTimer = useRef(null);

  // ── Rental Property dropdown: search + infinite scroll pagination state ──
  const [rentalPropertyOptions, setRentalPropertyOptions] = useState([]);
  const [rentalPropertySearchTerm, setRentalPropertySearchTerm] = useState("");
  const [rentalPropertyPage, setRentalPropertyPage] = useState(1);
  const [rentalPropertyTotal, setRentalPropertyTotal] = useState(0);
  const [rentalPropertyFetching, setRentalPropertyFetching] = useState(false);
  const rentalPropertySearchTimer = useRef(null);

  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [categorySections, setCategorySections] = useState([]);
  const [projectCategoriesForCoupon, setProjectCategoriesForCoupon] = useState([]);

  const [selectedBuilderId, setSelectedBuilderId] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedProjectCategories, setSelectedProjectCategories] = useState([]);

  const [selectedProjectCategoryDropdownId, setSelectedProjectCategoryDropdownId] = useState("");

  const [fetchedProjects, setFetchedProjects] = useState([]);
  const [catalogueOptions, setCatalogueOptions] = useState([]);
  const [selectedCatalogueId, setSelectedCatalogueId] = useState("");

  const [selectedUnitId, setSelectedUnitId] = useState([]);
  const [unitTypeOptions, setUnitTypeOptions] = useState([]);
  const [detailedUnitOptions, setDetailedUnitOptions] = useState([]);
  const [selectedDetailedUnitId, setSelectedDetailedUnitId] = useState([]);
  const [selectedUnitDetailsInfo, setSelectedUnitDetailsInfo] = useState([]);

  const [setSelectedFurnishingStatuses] = useState([]);

  const [unitDiscounts, setUnitDiscounts] = useState({});
  const [combinedItems, setCombinedItems] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { agents } = useSelector((state) => state.agents);
  const { countryList = { data: [] } } = useSelector((state) => state.country || {});
  const { enumsData = {} } = useSelector((state) => state.enumsData || {});
  const categoryList = useSelector((state) => state.coupons.categoryList || {});
  const builderList = useSelector((state) => state.coupons.builderList?.data || []);

  // ── Fetch a page of users (search + pagination), same pattern as ListUsers.jsx ──
  const fetchUserOptions = useCallback(
    async (page, search, append = false) => {
      setUserFetching(true);
      try {
        const payload = {
          page,
          limit: DROPDOWN_PAGE_LIMIT,
          is_deleted: false,
          filter: search,
        };
        const response = await dispatch(getUsersList(payload));
        const list = Array.isArray(response?.data) ? response.data : [];
        const total = response?.total || 0;

        setUserOptions((prev) => (append ? [...prev, ...list] : list));
        setUserTotal(total);
        setUserPage(page);
      } catch {
        if (!append) setUserOptions([]);
      } finally {
        setUserFetching(false);
      }
    },
    [dispatch]
  );

  const propStatus = enumsData?.data?.PropertyStatus;

  // ── Fetch a page of rental properties (search + pagination), same pattern as ListRentalProperties.jsx ──
  const fetchRentalPropertyOptions = useCallback(
    async (page, search, append = false) => {
      setRentalPropertyFetching(true);
      try {
        const payload = {
          page,
          limit: DROPDOWN_PAGE_LIMIT,
          is_deleted: false,
          business_type: "rental",
          filter: search,
          status: propStatus,
        };
        const response = await dispatch(viewPropertyList(payload));
        const list = Array.isArray(response?.data) ? response.data : [];
        const total = response?.total || 0;

        setRentalPropertyOptions((prev) => (append ? [...prev, ...list] : list));
        setRentalPropertyTotal(total);
        setRentalPropertyPage(page);
      } catch {
        if (!append) setRentalPropertyOptions([]);
      } finally {
        setRentalPropertyFetching(false);
      }
    },
    [dispatch, propStatus]
  );

  useEffect(() => {
    dispatch(getEnums());
    dispatch(viewCountryListPagination());
    dispatch(getCategoryList({ page: 1, limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (couponState.country) {
      dispatch(getBuilderList({ page: 1, limit: 1000, country_id: couponState.country }));
    }
  }, [dispatch, couponState.country]);

  useEffect(() => {
    if (couponState.use_for === "agent") {
      dispatch(AgentList({ page: 1, limit: 1000, is_deleted: false, role: ["exclusive partner"] }));
    } else {
      setCouponState((prev) => ({ ...prev, agent_id: "" }));
    }
  }, [dispatch, couponState.use_for]);

  // load the first page of users + rental properties as soon as that type is selected
  useEffect(() => {
    if (entityType === "rental_property") {
      fetchUserOptions(1, "", false);
      fetchRentalPropertyOptions(1, "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType]);

  // debounce user search — resets to page 1 on every new search term
  useEffect(() => {
    if (entityType !== "rental_property") return;
    if (userSearchTimer.current) clearTimeout(userSearchTimer.current);
    userSearchTimer.current = setTimeout(() => {
      fetchUserOptions(1, userSearchTerm, false);
    }, 500);
    return () => clearTimeout(userSearchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearchTerm]);

  // debounce rental property search — resets to page 1 on every new search term
  useEffect(() => {
    if (entityType !== "rental_property") return;
    if (rentalPropertySearchTimer.current) clearTimeout(rentalPropertySearchTimer.current);
    rentalPropertySearchTimer.current = setTimeout(() => {
      fetchRentalPropertyOptions(1, rentalPropertySearchTerm, false);
    }, 500);
    return () => clearTimeout(rentalPropertySearchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentalPropertySearchTerm]);

  const handleUserPopupScroll = (e) => {
    const target = e.target;
    const nearBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 20;
    const hasMore = userOptions.length < userTotal;
    if (nearBottom && hasMore && !userFetching) {
      fetchUserOptions(userPage + 1, userSearchTerm, true);
    }
  };

  const handleRentalPropertyPopupScroll = (e) => {
    const target = e.target;
    const nearBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 20;
    const hasMore = rentalPropertyOptions.length < rentalPropertyTotal;
    if (nearBottom && hasMore && !rentalPropertyFetching) {
      fetchRentalPropertyOptions(rentalPropertyPage + 1, rentalPropertySearchTerm, true);
    }
  };

  // reset the "project" side state and the "rental property" side state when switching type
  const handleEntityTypeChange = (e) => {
    const value = e.target.value;
    setEntityType(value);

    if (value === "rental_property") {
      // clear project-flow selections
      setSelectedBuilderId("");
      setSelectedCategoryId("");
      setSelectedProjectId("");
      setFetchedProjects([]);
      setSelectedProjectCategories([]);
      setProjectCategoriesForCoupon([]);
      setSelectedProjectCategoryDropdownId("");
      setCatalogueOptions([]);
      setSelectedCatalogueId("");
      setDetailedUnitOptions([]);
      setUnitTypeOptions([]);
      setSelectedUnitId([]);
      setSelectedDetailedUnitId([]);
      setSelectedUnitDetailsInfo([]);
      setUnitDiscounts({});
      setCategorySections([]);
      setCouponState((prev) => ({ ...prev, project_id: "", project_type: "" }));
    } else {
      // clear rental-property-flow selections
      setSelectedUserId("");
      setSelectedRentalPropertyId("");
      setUserOptions([]);
      setUserSearchTerm("");
      setUserPage(1);
      setUserTotal(0);
      setRentalPropertyOptions([]);
      setRentalPropertySearchTerm("");
      setRentalPropertyPage(1);
      setRentalPropertyTotal(0);
    }
  };

  useEffect(() => {
    const fetchProjects = async () => {
      if (entityType !== "project") return;
      if (!selectedBuilderId || !selectedCategoryId) {
        setFetchedProjects([]);
        setSelectedProjectId("");
        setSelectedProjectCategories([]);
        setCatalogueOptions([]);
        setSelectedCatalogueId("");
        setDetailedUnitOptions([]);
        setUnitTypeOptions([]);
        setSelectedUnitId([]);
        setCouponState((prev) => ({ ...prev, project_id: "", project_type: "" }));
        return;
      }

      const payload = {
        builder_id: selectedBuilderId,
        project_type: selectedCategoryId,
        status: ["published"],
        page: 1,
        limit: 30,
        is_deleted: false,
      };

      const response = await dispatch(getProjectListByBuilder(payload));
      if (response && response.data) {
        setFetchedProjects(response.data);
      } else {
        setFetchedProjects([]);
      }

      setSelectedProjectId("");
      setSelectedProjectCategories([]);
      setCatalogueOptions([]);
      setSelectedCatalogueId("");
      setDetailedUnitOptions([]);
      setUnitTypeOptions([]);
      setSelectedUnitId([]);
      setCouponState((prev) => ({ ...prev, project_id: "", project_type: "" }));
    };

    fetchProjects();
  }, [selectedBuilderId, selectedCategoryId, dispatch, entityType]);

  useEffect(() => {
    if (
      entityType === "project" &&
      selectedProjectId &&
      selectedCategoryId &&
      selectedCatalogueId &&
      selectedProjectCategories.length > 0 &&
      couponState.project_type
    ) {
      const category_id = selectedProjectCategories[0]?._id;
      if (!category_id) {
        setDetailedUnitOptions([]);
        setUnitTypeOptions([]);
        return;
      }
      const isCommercial =
        categoryList.data
          ?.find((cat) => cat._id === selectedCategoryId)
          ?.name.toLowerCase() === "commercial";

      const payload = {
        project_id: couponState.project_id,
        project_type: couponState.project_type,
        catalogue_id: selectedCatalogueId,
        ...(isCommercial ? {} : { category_id }),
      };

      dispatch(getUnitList(payload)).then((res) => {
        if (res && res.data && Array.isArray(res.data.result)) {
          const unitTypes = res.data.result.map((group) => ({
            label: group.units ? group.units.toUpperCase() : "",
            value: group.units || "",
          }));
          setUnitTypeOptions(unitTypes);

          const combined = res.data.result.reduce((acc, group) => {
            if (Array.isArray(group.items)) acc.push(...group.items);
            return acc;
          }, []);
          setCombinedItems(combined);

          const detailedOptions = combined.map((unit) => ({
            label: `Floor: ${unit.floor} | Unit: ${unit.unitno} | Furnishing: ${unit.furnishingstatus}`,
            value: unit.catalogue_unit_id,
            unitType: unit.unit ? unit.unit : unit.type,
          }));
          setDetailedUnitOptions(detailedOptions);
        } else {
          setUnitTypeOptions([]);
          setDetailedUnitOptions([]);
          setCombinedItems([]);
        }
      });
    } else {
      setDetailedUnitOptions([]);
      setUnitTypeOptions([]);
      setCombinedItems([]);
    }
    setSelectedUnitId([]);
    setUnitDiscounts({});
  }, [
    selectedProjectId,
    selectedCategoryId,
    selectedCatalogueId,
    selectedProjectCategories,
    couponState.project_type,
    dispatch,
    couponState.project_id,
    categoryList.data,
    entityType,
  ]);

  useEffect(() => {
    setUnitDiscounts((prev) => {
      const copy = { ...prev };
      selectedUnitId.forEach((unitId) => {
        if (!copy[unitId]) {
          copy[unitId] = { discount_type: "", discount_in: "", value: 0, value_upto_show: 0, max_value: 0 };
        }
      });
      Object.keys(copy).forEach((unitId) => {
        if (!selectedUnitId.includes(unitId)) delete copy[unitId];
      });
      return copy;
    });
  }, [selectedUnitId]);

  const handleUnitDiscountChange = (unitId, fieldName, fieldValue) => {
    setUnitDiscounts((prev) => {
      const copy = { ...prev };
      if (!copy[unitId]) {
        copy[unitId] = { discount_type: "", discount_in: "", value: 0, value_upto_show: 0, max_value: 0 };
      }

      if (fieldName === "max_value") {
        copy[unitId][fieldName] = Number(fieldValue);
        return copy;
      }

      if ((fieldName === "value" || fieldName === "value_upto_show") && copy[unitId].discount_in === "percent") {
        let val = Number(fieldValue);
        if (val > 100) val = 100;
        copy[unitId][fieldName] = val;
      } else if (fieldName === "discount_in") {
        copy[unitId][fieldName] = fieldValue;
        copy[unitId]["value"] = 0;
        copy[unitId]["value_upto_show"] = 0;
      } else {
        copy[unitId][fieldName] =
          fieldName === "value" || fieldName === "value_upto_show"
            ? Number(fieldValue)
            : fieldValue;
      }
      return copy;
    });
  };

  const handleDetailedUnitChange = (selectedValues) => {
    setSelectedDetailedUnitId(selectedValues);
    const selectedInfo = detailedUnitOptions
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => {
        const unitData = combinedItems.find((item) => item.catalogue_unit_id === option.value);
        return {
          catalogue_unit_id: option.value,
          unitno: unitData?.unitno || "",
          floor: unitData?.floor || "",
          furnishingstatus: unitData?.furnishingstatus || "",
        };
      });
    setSelectedUnitDetailsInfo(selectedInfo);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      let couponData = {
        code: couponState.code,
        use_for: couponState.use_for,
        ...(couponState.use_for === "agent" && { agent: couponState.agent_id }),
        no_of_usage: couponState.no_of_usage,
        country_id: couponState.country,
        start_date: couponState.start_date,
        end_date: couponState.end_date,
        set_primary: couponState.set_primary,
        description: couponState.description,
      };

      if (entityType === "rental_property") {
        // user is optional — only include it in the payload when actually selected
        couponData.rental_property = selectedRentalPropertyId;
        if (selectedUserId) {
          couponData.user = selectedUserId;
        }
      } else {
        couponData.project_id = couponState.project_id;
        couponData.project_type = couponState.project_type;
        couponData.catalogue = selectedCatalogueId || undefined;
        couponData.builder_id = selectedBuilderId;
        couponData.project_categories =
          isCommercialCategory && selectedProjectCategoryDropdownId
            ? selectedProjectCategories.filter((cat) => cat._id === selectedProjectCategoryDropdownId)
            : projectCategoriesForCoupon;
        couponData.categories = categorySections;
      }

      if (entityType === "project" && selectedUnitId.length > 0) {
        const discounts = selectedUnitId.map((unitId) => {
          const discountData = unitDiscounts[unitId] || {};
          return {
            catalogue_unit_id: unitId,
            unit: unitId,
            discount_type: discountData.discount_type || "",
            discount_in: discountData.discount_in || "",
            value: discountData.value || 0,
            value_upto_show: discountData.value_upto_show || 0,
            max_value: discountData.max_value || 0,
          };
        });
        couponData.units = selectedUnitId;
        couponData.unitno = selectedUnitDetailsInfo;
        couponData.discounts = discounts;
      } else {
        couponData.discount_type = couponState.discount_type || "";
        couponData.discount_in = couponState.discount_in || "";
        couponData.value = couponState.value || 0;
        couponData.value_upto_show = couponState.value_upto_show || 0;
        couponData.max_value = couponState.max_value || 0;
      }

      const response = await dispatch(addCoupons(couponData));
      if (response?.statusCode === 200) {
        navigate("/dealsAndOffers");
        setCouponState({
          code: Math.random().toString(36).substring(2, 18).toUpperCase(),
          project_id: "", project_type: "", no_of_usage: "", country: "",
          start_date: "", end_date: "", discount_type: "", discount_in: "", use_for: "", agent_id: "",
          value: "", value_upto_show: "", max_value: "", description: "", set_primary: false,
        });
        setEntityType("project");
        setSelectedUserId("");
        setSelectedRentalPropertyId("");
        setUserOptions([]); setUserSearchTerm(""); setUserPage(1); setUserTotal(0);
        setRentalPropertyOptions([]); setRentalPropertySearchTerm(""); setRentalPropertyPage(1); setRentalPropertyTotal(0);
        setFetchedProjects([]); setSelectedProjectId(""); setSelectedBuilderId("");
        setSelectedCategoryId(""); setCategorySections([]); setProjectCategoriesForCoupon([]);
        setSelectedProjectCategories([]); setSelectedProjectCategoryDropdownId("");
        setCatalogueOptions([]); setSelectedCatalogueId(""); setDetailedUnitOptions([]);
        setUnitTypeOptions([]); setSelectedUnitId([]); setSelectedDetailedUnitId([]);
        setSelectedUnitDetailsInfo([]); setSelectedFurnishingStatuses([]); setUnitDiscounts({});
      }
    } catch (error) {
      console.error(error?.message);
    }
  };

  const handleCategoryChange = (e) => {
    const selectedId = e.target.value;
    setSelectedCategoryId(selectedId);
    setCategorySections([selectedId]);
  };

  const handleProjectChange = (e) => {
    const selectedProjectId = e.target.value;
    const selectedProject = fetchedProjects.find((project) => project._id === selectedProjectId);

    if (!selectedProject) {
      setSelectedProjectId("");
      setCouponState((prev) => ({ ...prev, project_id: "", project_type: "" }));
      setSelectedProjectCategories([]);
      setProjectCategoriesForCoupon([]);
      setCatalogueOptions([]);
      setSelectedCatalogueId("");
      setDetailedUnitOptions([]);
      setUnitTypeOptions([]);
      setSelectedUnitId([]);
      setUnitDiscounts({});
      return;
    }

    setSelectedProjectId(selectedProjectId);
    setCouponState((prev) => ({
      ...prev,
      project_id: selectedProject._id,
      project_type: selectedProject.project_type?._id || "",
    }));
    setSelectedProjectCategories(selectedProject.categories || []);
    setProjectCategoriesForCoupon(selectedProject.categories || []);

    const selectedCategory = categoryList.data?.find((cat) => cat._id === selectedCategoryId);
    const isCommercial = selectedCategory?.name?.toLowerCase() === "commercial";

    if (isCommercial) {
      setCatalogueOptions(
        selectedProject.catalogue_list.map((item) => ({
          label: item.tower_name || "No Tower Name",
          value: item._id,
        }))
      );
    } else {
      const categoryNames = selectedProject.categories.map((cat) => cat.name?.toLowerCase());
      if (categoryNames.includes("plot")) {
        setCatalogueOptions(selectedProject.catalogue_list.map((item) => ({ label: item.site_name, value: item._id })));
      } else if (categoryNames.includes("apartment") || categoryNames.includes("villa")) {
        setCatalogueOptions(
          selectedProject.catalogue_list.map((item) => ({
            label: item.tower_name || item.catalogue_name || "No Catalogue Name",
            value: item._id,
          }))
        );
      } else {
        setCatalogueOptions([]);
      }
    }

    setSelectedCatalogueId("");
    setDetailedUnitOptions([]);
    setUnitTypeOptions([]);
    setSelectedUnitId([]);
    setUnitDiscounts({});
  };

  const handleAddChange = (event) => {
    const { name, value } = event.target;
    setCouponState((prev) => {
      let updatedState = { ...prev, [name]: value };
      if (name === "start_date" && new Date(updatedState.end_date) < new Date(value)) {
        updatedState.end_date = value;
      }
      return updatedState;
    });
  };

  const filteredDetailedUnitOptions = useMemo(() => {
    if (selectedUnitId.length === 0 || detailedUnitOptions.length === 0) return [];
    return detailedUnitOptions.filter((option) => selectedUnitId.includes(option.unitType));
  }, [selectedUnitId, detailedUnitOptions]);

  const handleUnitChange = (e) => {
    const value = e.target.value;
    const selectedUnits = Array.isArray(value) ? value : [value];
    setSelectedUnitId(selectedUnits);

    const filteredDetailed = selectedDetailedUnitId.filter((detailedId) => {
      const option = detailedUnitOptions.find((opt) => opt.value === detailedId);
      return option && selectedUnits.includes(option.unitType);
    });
    setSelectedDetailedUnitId(filteredDetailed);
    handleDetailedUnitChange(filteredDetailed);
  };

  const filterDetailedByFurnishing = (status) => {
    const options = detailedUnitOptions.filter((opt) => {
      const match = opt.label.match(/Furnishing: ([^|]+)/i);
      return (
        match !== null &&
        typeof match[1] === "string" &&
        match[1].toLowerCase().replace(/\s/g, "") === status &&
        selectedUnitId.includes(opt.unitType)
      );
    });
    const values = options.map((opt) => opt.value);
    setSelectedDetailedUnitId(values);
    handleDetailedUnitChange(values);
  };

  const isCommercialCategory = useMemo(() => {
    const selectedCategory = categoryList.data?.find((cat) => cat._id === selectedCategoryId);
    return selectedCategory?.name?.toLowerCase() === "commercial";
  }, [selectedCategoryId, categoryList]);

  return (
    <MainLayout>
      <div className="acp-wrapper">

        {/* ── Header ── */}
        <div className="acp-header">
          <div className="acp-header-left">
            <div className="acp-header-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </div>
            <div>
              <h1 className="acp-title">Create Coupon</h1>
              <p className="acp-subtitle">Configure and launch a new coupon</p>
            </div>
          </div>
          <button className="acp-view-btn" type="button" onClick={() => navigate("/dealsAndOffers")}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
            </svg>
            View Coupons
          </button>
        </div>

        <form onSubmit={handleSubmit} className="acp-form">

          {/* ── Coupon Code (read-only) ── */}
          <div className="acp-code-banner">
            <div className="acp-code-label">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M7 15h0M12 15h0M17 15h0M7 9h10" />
              </svg>
              Coupon Code
            </div>
            <span className="acp-code-value">{couponState.code}</span>
            <span className="acp-code-badge">Auto-generated</span>
          </div>

          {/* ── Section: Basic Info ── */}
          <div className="acp-card">
            <div className="acp-card-header">
              <span className="acp-card-badge">01</span>
              <h2 className="acp-card-title">Basic Information</h2>
            </div>
            <div className={`acp-grid-role ${couponState.use_for === "agent" ? "has-agent" : ""}`} style={{ marginBottom: "18px" }}>
              <div className="acp-field">
                <label className="acp-label">Role <span className="acp-req">*</span></label>
                <select
                  className="acp-select"
                  name="use_for"
                  value={couponState.use_for}
                  onChange={handleAddChange}
                >
                  <option value="">Select a Role</option>
                  <option value="user">User</option>
                  <option value="agent">Agent</option>
                  <option value="kiosk">Kiosk</option>
                </select>
              </div>

              {couponState.use_for === "agent" && (
                <div className="acp-field">
                  <label className="acp-label">Select Agent <span className="acp-req">*</span></label>
                  <select
                    className="acp-select"
                    name="agent_id"
                    value={couponState.agent_id}
                    onChange={handleAddChange}
                  >
                    <option value="">Select an Agent</option>
                    {Array.isArray(agents?.data) && agents.data.map((agent) => (
                      <option key={agent._id} value={agent._id}>
                        {agent.name || agent.full_name || agent.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Type dropdown — Project or Rental Property */}
              <div className="acp-field">
                <label className="acp-label">Type <span className="acp-req">*</span></label>
                <select
                  className="acp-select"
                  name="entity_type"
                  value={entityType}
                  onChange={handleEntityTypeChange}
                >
                  <option value="project">Project</option>
                  <option value="rental_property">Rental Property</option>
                </select>
              </div>
            </div>

            {entityType === "project" ? (
              <div className="acp-grid acp-grid-2">
                <div className="acp-field">
                  <label className="acp-label">Country <span className="acp-req">*</span></label>
                  <select
                    className="acp-select"
                    name="country"
                    value={couponState.country}
                    onChange={handleAddChange}
                  >
                    <option value="">Select an Option</option>
                    {Array.isArray(countryList?.data)
                      ? countryList.data.map((country) => (
                        <option key={country._id} value={country._id}>{country.country_name}</option>
                      ))
                      : null}
                  </select>
                </div>

                <div className="acp-field">
                  <label className="acp-label">Select Builder <span className="acp-req">*</span></label>
                  <select
                    className="acp-select"
                    name="builder_id"
                    value={selectedBuilderId}
                    onChange={(e) => setSelectedBuilderId(e.target.value)}
                    disabled={!couponState.country}
                  >
                    <option value="">Select a Builder</option>
                    {builderList.length > 0
                      ? builderList.map((builder) => (
                        <option key={builder._id} value={builder._id}>{builder.brand_name}</option>
                      ))
                      : null}
                  </select>
                </div>

                <div className="acp-field">
                  <label className="acp-label">Category Section <span className="acp-req">*</span></label>
                  <select
                    className="acp-select"
                    name="category_id"
                    value={selectedCategoryId}
                    onChange={handleCategoryChange}
                  >
                    <option value="">Select a Category Section</option>
                    {categoryList.data && Array.isArray(categoryList.data)
                      ? categoryList.data.map((cat) => (
                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                      ))
                      : null}
                  </select>
                </div>

                <div className="acp-field">
                  <label className="acp-label">Select Project <span className="acp-req">*</span></label>
                  <select
                    className="acp-select"
                    name="project_id"
                    value={selectedProjectId}
                    onChange={handleProjectChange}
                  >
                    <option value="">Select an Option</option>
                    {Array.isArray(fetchedProjects) && fetchedProjects.length > 0
                      ? fetchedProjects.map((project) => (
                        <option key={project._id} value={project._id}>{project.project_name}</option>
                      ))
                      : <option value="" disabled>No data</option>}
                  </select>
                </div>

                {!isCommercialCategory && (
                  <div className="acp-field">
                    <label className="acp-label">Project Category <span className="acp-req">*</span></label>
                    <select
                      className="acp-select"
                      name="project_category_id"
                      value={selectedProjectCategoryDropdownId}
                      onChange={(e) => setSelectedProjectCategoryDropdownId(e.target.value)}
                    >
                      <option value="">Select Project Category</option>
                      {selectedProjectCategories.map((cat) => (
                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="acp-field">
                  <label className="acp-label">Select Catalogue</label>
                  <select
                    className="acp-select"
                    name="catalogue_id"
                    value={selectedCatalogueId}
                    onChange={(e) => setSelectedCatalogueId(e.target.value)}
                  >
                    <option value="">Select Catalogue</option>
                    {catalogueOptions.length > 0
                      ? catalogueOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))
                      : <option value="" disabled>No data</option>}
                  </select>
                </div>

                <div className="acp-field">
                  <label className="acp-label">Select Type</label>
                  <select
                    className="acp-select"
                    name="unit_id"
                    value={selectedUnitId[0] || ""}
                    onChange={handleUnitChange}
                  >
                    <option value="">Select Type</option>
                    {unitTypeOptions.length > 0
                      ? unitTypeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))
                      : <option value="" disabled>No data</option>}
                  </select>
                </div>
              </div>
            ) : (
              // Rental Property flow — searchable, infinite-scroll User + Rental Property dropdowns
              <div className="acp-grid acp-grid-2">
                <div className="acp-field">
                  <label className="acp-label">Select User <span className="acp-optional">(optional)</span></label>
                  <Select
                    className="acp-antd-select"
                    style={{ width: "100%" }}
                    showSearch
                    allowClear
                    placeholder="Search by name / email / phone"
                    value={selectedUserId || undefined}
                    filterOption={false}
                    onSearch={(val) => setUserSearchTerm(val)}
                    onChange={(val) => setSelectedUserId(val || "")}
                    onPopupScroll={handleUserPopupScroll}
                    loading={userFetching}
                    notFoundContent={userFetching ? "Loading..." : "No data"}
                    optionLabelProp="label"
                  >
                    {userOptions.map((user) => {
                      const userLabel = user.name || user.email || user.contact_number || "Unnamed User";
                      const userId = user.user_id;
                      const hasValidId = userId && user._id !== userId;
                      const secondaryInfo = user.email && user.name ? user.email : user.contact_number;

                      return (
                        <Select.Option
                          key={user._id}
                          value={user._id}
                          label={hasValidId ? `${userLabel} (${userId})` : userLabel}
                        >
                          <div className="acp-property-row">
                            <div className="acp-property-row-main">
                              <span className="acp-property-name">{userLabel}</span>
                              <span className="acp-property-id-tag">
                                {hasValidId ? `#${userId}` : ""}
                                {hasValidId && secondaryInfo ? " · " : ""}
                                {secondaryInfo || ""}
                              </span>
                            </div>
                          </div>
                        </Select.Option>
                      );
                    })}
                  </Select>
                </div>

                <div className="acp-field">
                  <label className="acp-label">Select Rental Property <span className="acp-req">*</span></label>
                  <Select
                    className="acp-antd-select"
                    style={{ width: "100%" }}
                    showSearch
                    allowClear
                    placeholder="Search rental property"
                    value={selectedRentalPropertyId || undefined}
                    filterOption={false}
                    onSearch={(val) => setRentalPropertySearchTerm(val)}
                    onChange={(val) => setSelectedRentalPropertyId(val || "")}
                    onPopupScroll={handleRentalPropertyPopupScroll}
                    loading={rentalPropertyFetching}
                    notFoundContent={rentalPropertyFetching ? "Loading..." : "No data"}
                    optionLabelProp="label"
                  >
                    {rentalPropertyOptions.map((property) => {
                      const propertyLabel = property.property_name || property.title || "Untitled Property";
                      const propertyId = property.property_id;
                      const hasValidId = propertyId && property._id !== propertyId;

                      return (
                        <Select.Option
                          key={property._id}
                          value={property._id}
                          label={hasValidId ? `${propertyLabel} (${propertyId})` : propertyLabel}
                        >
                          <div className="acp-property-row">
                            <div className="acp-property-row-main">
                              <span className="acp-property-name">{propertyLabel}</span>
                              {hasValidId && <span className="acp-property-id-tag">#{propertyId}</span>}
                            </div>
                            {property.business_type && (
                              <span className="acp-business-type-tag">{ToFirstCapital(property.business_type)}</span>
                            )}
                          </div>
                        </Select.Option>
                      );
                    })}
                  </Select>
                </div>
              </div>
            )}
          </div>

          {/* ── Section: Per-Unit Discounts (project flow only) ── */}
          {entityType === "project" && selectedUnitId.length > 0 && (
            <div className="acp-card">
              <div className="acp-card-header">
                <span className="acp-card-badge">02</span>
                <h2 className="acp-card-title">Discount Details Per Unit</h2>
              </div>
              <div className="acp-unit-discounts">
                {selectedUnitId.map((unitId) => {
                  const discount = unitDiscounts[unitId] || {
                    discount_type: "", discount_in: "", value: 0, value_upto_show: 0, max_value: 0,
                  };
                  const isPercent = discount.discount_in === "percent";

                  return (
                    <div key={unitId} className="acp-unit-block">
                      <div className="acp-unit-block-label">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <path d="M9 9h6M9 12h6M9 15h4" />
                        </svg>
                        Unit: {unitId}
                      </div>
                      <div className="acp-grid acp-grid-2">
                        <div className="acp-field">
                          <label className="acp-label">Discount Type <span className="acp-req">*</span></label>
                          <select
                            className="acp-select"
                            value={discount.discount_type}
                            onChange={(e) => handleUnitDiscountChange(unitId, "discount_type", e.target.value)}
                          >
                            <option value="">Select Discount Type</option>
                            {enumsData?.data?.DiscountType
                              ? Object.entries(enumsData.data.DiscountType).map(([_key, value]) => (
                                <option key={value} value={value}>{ToFirstCapital(value)}</option>
                              ))
                              : null}
                          </select>
                        </div>

                        <div className="acp-field">
                          <label className="acp-label">Discount Format <span className="acp-req">*</span></label>
                          <select
                            className="acp-select"
                            value={discount.discount_in}
                            onChange={(e) => handleUnitDiscountChange(unitId, "discount_in", e.target.value)}
                          >
                            <option value="">Select Discount Format</option>
                            {enumsData?.data?.DiscountFormat
                              ? Object.entries(enumsData.data.DiscountFormat).map(([_key, value]) => (
                                <option key={value} value={value}>{ToFirstCapital(value)}</option>
                              ))
                              : null}
                          </select>
                        </div>

                        <div className="acp-field">
                          <label className="acp-label">Coupon Value</label>
                          <input
                            className="acp-input"
                            type="number"
                            min={0}
                            max={isPercent ? 100 : undefined}
                            value={discount.value === 0 ? "" : discount.value}
                            onChange={(e) => handleUnitDiscountChange(unitId, "value", e.target.value)}
                            placeholder="Enter value"
                          />
                        </div>

                        {discount.discount_type === "upto" && discount.discount_in === "percent" && (
                          <div className="acp-field">
                            <label className="acp-label">Max Value</label>
                            <input
                              className="acp-input"
                              type="number"
                              min={0}
                              max={100}
                              value={discount.max_value === 0 ? "" : discount.max_value}
                              onChange={(e) => handleUnitDiscountChange(unitId, "max_value", e.target.value)}
                              placeholder="Enter max value"
                            />
                          </div>
                        )}

                        {discount.discount_type === "upto" && (
                          <div className="acp-field">
                            <label className="acp-label">Show Upto Value</label>
                            <input
                              className="acp-input"
                              type="number"
                              min={0}
                              max={isPercent ? 100 : undefined}
                              value={discount.value_upto_show === 0 ? "" : discount.value_upto_show}
                              onChange={(e) => handleUnitDiscountChange(unitId, "value_upto_show", e.target.value)}
                              placeholder="Enter upto value"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Section: Single Discount (rental property flow always uses this; project flow uses it when no units selected) ── */}
          {(entityType === "rental_property" || selectedUnitId.length === 0) && (
            <div className="acp-card">
              <div className="acp-card-header">
                <span className="acp-card-badge">02</span>
                <h2 className="acp-card-title">Discount Configuration</h2>
              </div>
              <div className="acp-grid acp-grid-2">
                <div className="acp-field">
                  <label className="acp-label">Discount Type</label>
                  <select className="acp-select" name="discount_type" value={couponState.discount_type || ""} onChange={handleAddChange}>
                    <option value="">Select Discount Type</option>
                    {enumsData?.data?.DiscountType
                      ? Object.entries(enumsData.data.DiscountType).map(([_key, value]) => (
                        <option key={value} value={value}>{ToFirstCapital(value)}</option>
                      ))
                      : null}
                  </select>
                </div>

                <div className="acp-field">
                  <label className="acp-label">Discount Format</label>
                  <select className="acp-select" name="discount_in" value={couponState.discount_in || ""} onChange={handleAddChange}>
                    <option value="">Select Discount Format</option>
                    {enumsData?.data?.DiscountFormat
                      ? Object.entries(enumsData.data.DiscountFormat).map(([_key, value]) => (
                        <option key={value} value={value}>{ToFirstCapital(value)}</option>
                      ))
                      : null}
                  </select>
                </div>

                <div className="acp-field">
                  <label className="acp-label">Coupon Value</label>
                  <input
                    className="acp-input"
                    type="number"
                    name="value"
                    value={couponState.value || ""}
                    min={0}
                    max={couponState.discount_in === "percent" ? 100 : undefined}
                    onChange={(e) => {
                      let val = Number(e.target.value);
                      if (couponState.discount_in === "percent" && val > 100) val = 100;
                      handleAddChange({ target: { name: "value", value: val } });
                    }}
                    placeholder="Enter value"
                  />
                </div>

                {couponState.discount_type === "upto" && couponState.discount_in === "percent" && (
                  <div className="acp-field">
                    <label className="acp-label">Max Value</label>
                    <input
                      className="acp-input"
                      type="number"
                      name="max_value"
                      min={0}
                      max={100}
                      value={couponState.max_value || ""}
                      onChange={handleAddChange}
                      placeholder="Enter max value"
                    />
                  </div>
                )}

                {couponState.discount_type === "upto" && (
                  <div className="acp-field">
                    <label className="acp-label">Show Upto Value</label>
                    <input
                      className="acp-input"
                      type="number"
                      name="value_upto_show"
                      value={couponState.value_upto_show || ""}
                      min={0}
                      max={couponState.discount_in === "percent" ? 100 : undefined}
                      onChange={(e) => {
                        let val = Number(e.target.value);
                        if (couponState.discount_in === "percent" && val > 100) val = 100;
                        handleAddChange({ target: { name: "value_upto_show", value: val } });
                      }}
                      placeholder="Enter upto value"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Section: Unit Details (project flow only) ── */}
          {entityType === "project" && (
            <div className="acp-card">
              <div className="acp-card-header">
                <span className="acp-card-badge">03</span>
                <h2 className="acp-card-title">Unit Details</h2>
              </div>

              <div className="acp-field">
                <label className="acp-label">
                  Select Unit Detail (Floor | Unit | Furnishing)
                  {selectedDetailedUnitId.length > 0 && (
                    <span className="acp-multi-count">{selectedDetailedUnitId.length} selected</span>
                  )}
                </label>
                <select
                  className="acp-select acp-select--multi"
                  name="detailed_unit_id"
                  multiple
                  value={selectedDetailedUnitId}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                    handleDetailedUnitChange(selected);
                  }}
                  size={Math.min(Math.max(filteredDetailedUnitOptions.length, 3), 8)}
                >
                  {filteredDetailedUnitOptions.length > 0
                    ? filteredDetailedUnitOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))
                    : <option value="" disabled>No data available</option>}
                </select>
                <p className="acp-multi-hint">Hold Ctrl / Cmd to select multiple units</p>
              </div>

              {selectedUnitId.length > 0 && (
                <div className="acp-furnishing-row">
                  <span className="acp-furnishing-label">Quick select:</span>
                  <button className="acp-furnish-btn" type="button" onClick={() => filterDetailedByFurnishing("furnished")}>
                    Furnished
                  </button>
                  <button className="acp-furnish-btn" type="button" onClick={() => filterDetailedByFurnishing("semifurnished")}>
                    Semi-Furnished
                  </button>
                  <button className="acp-furnish-btn" type="button" onClick={() => filterDetailedByFurnishing("unfurnished")}>
                    Un-Furnished
                  </button>
                  <button
                    type="button"
                    className="acp-furnish-btn acp-furnish-btn--all"
                    onClick={() => {
                      const allOptionsValues = detailedUnitOptions
                        .filter((opt) => selectedUnitId.includes(opt.unitType))
                        .map((option) => option.value);
                      setSelectedDetailedUnitId(allOptionsValues);
                      handleDetailedUnitChange(allOptionsValues);
                    }}
                  >
                    Select All
                  </button>
                  {selectedDetailedUnitId.length > 0 && (
                    <button
                      type="button"
                      className="acp-furnish-btn acp-furnish-btn--clear"
                      onClick={() => {
                        setSelectedDetailedUnitId([]);
                        setSelectedUnitDetailsInfo([]);
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Section: Validity & Usage ── */}
          <div className="acp-card">
            <div className="acp-card-header">
              <span className="acp-card-badge">04</span>
              <h2 className="acp-card-title">Validity & Usage</h2>
            </div>
            <div className="acp-grid acp-grid-2">
              <div className="acp-field">
                <label className="acp-label">Valid From <span className="acp-req">*</span></label>
                <input
                  className="acp-input"
                  type="date"
                  name="start_date"
                  value={couponState.start_date}
                  onChange={handleAddChange}
                  min={new Date().toISOString().split("T")[0]}
                  max={couponState.end_date}
                />
              </div>
              <div className="acp-field">
                <label className="acp-label">Valid Till <span className="acp-req">*</span></label>
                <input
                  className="acp-input"
                  type="date"
                  name="end_date"
                  value={couponState.end_date}
                  onChange={handleAddChange}
                  min={couponState.start_date || new Date().toISOString().split("T")[0]}
                />
              </div>
              <div className="acp-field">
                <label className="acp-label">No. of Usages <span className="acp-req">*</span></label>
                <input
                  className="acp-input"
                  type="number"
                  name="no_of_usage"
                  value={couponState.no_of_usage}
                  onChange={handleAddChange}
                  min={0}
                  placeholder="e.g. 100"
                />
              </div>
            </div>
          </div>

          {/* ── Section: Additional Info ── */}
          <div className="acp-card">
            <div className="acp-card-header">
              <span className="acp-card-badge">05</span>
              <h2 className="acp-card-title">Additional Info</h2>
            </div>

            <div className="acp-field">
              <label className="acp-label">Description</label>
              <textarea
                className="acp-textarea"
                name="description"
                value={couponState.description}
                onChange={handleAddChange}
                rows={3}
                placeholder="Add description for this coupon…"
              />
            </div>

            <div className="acp-toggle-row">
              <div className="acp-toggle-info">
                <p className="acp-toggle-title">Set as Primary</p>
                <p className="acp-toggle-hint">Mark this coupon as the primary offer (optional)</p>
              </div>
              <label className="acp-toggle">
                <input
                  type="checkbox"
                  name="set_primary"
                  checked={couponState.set_primary || false}
                  onChange={(e) =>
                    setCouponState((prev) => ({ ...prev, set_primary: e.target.checked }))
                  }
                />
                <span className="acp-toggle-slider"></span>
              </label>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="acp-footer">
            <Link to="/dealsAndOffers" className="acp-btn acp-btn--cancel">Cancel</Link>
            <button type="submit" className="acp-btn acp-btn--submit">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Save Coupon
            </button>
          </div>

        </form>
      </div>
    </MainLayout>
  );
};

export default AddCoupons;